import React from 'react'
import ModulePanel from '../../../ui/ModulePanel/ModulePanel'
import ModuleSpoiler from '../../../ui/ModuleSpoiler/ModuleSpoiler'
import Button from '../../../ui/Button/Button'
import Switch from '../../../ui/Switch/Switch'
import StatusMessage, { initStatusMessage, useStatusMessage } from '../../../ui/StatusMessage/StatusMessage'
import {
  getRewardGroupColumnLabel,
  getTierRangeLabel,
} from '../../../api/marketing/gifts/giftWithPurchaseConfigHelpers'
import {
  countFundItemsByAvailableFromTierId,
  countFundItemsByRewardGroupId,
} from '../../../api/marketing/gifts/giftFundHelpers'
import { useGiftsConfig } from './GiftsConfigContext'
import { useGiftsFund } from './GiftsFundContext'
import styles from './GiftsRules.module.scss'

const parseNonNegativeInteger = (raw) => {
  if (raw === '' || raw === null || raw === undefined) {
    return null
  }

  const value = Number(raw)
  if (!Number.isInteger(value) || value < 0) {
    return null
  }

  return value
}

const parseAmountInput = (raw, onValid) => {
  if (raw === '') {
    onValid(0)
    return
  }

  const parsed = parseNonNegativeInteger(raw)
  if (parsed === null) {
    return
  }

  onValid(parsed)
}

function GiftsRules() {
  const {
    draft,
    sortedTiers,
    sortedGroups,
    loadStatus,
    isDirty,
    isSaving,
    resetToBaseline,
    saveConfig,
    setApplyToOutlet,
    updateTierMinAmount,
    updateTierQuota,
    addTier,
    removeTier,
    updateGroupName,
    addGroup,
    removeGroup,
    moveGroup,
  } = useGiftsConfig()
  const { items: fundItems } = useGiftsFund()
  const [statusMessage, setStatusMessage] = useStatusMessage()

  const onCancelChanges = () => {
    resetToBaseline()
    setStatusMessage(initStatusMessage())
  }

  const onSave = async () => {
    const result = await saveConfig()

    if (!result.ok) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message: result.message || 'Не вдалося зберегти',
        closeAfter: 6000,
      })
      return
    }

    setStatusMessage({
      show: true,
      type: 'success',
      message: 'Дані збережено',
      closeAfter: 4000,
    })
  }

  const onRemoveTier = (tierId) => {
    if (draft.packaging?.freeFromTierId === tierId) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message:
          'Цю ступінь вибрано для безкоштовного пакування. Спочатку змініть привʼязку в «Подарункове пакування».',
        closeAfter: 6000,
      })
      return
    }

    const fundTierRefs = countFundItemsByAvailableFromTierId(fundItems, tierId)
    if (fundTierRefs > 0) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message: `Цю ступінь вибрано в ${fundTierRefs} позиціях фонду (включно з архівом). Спочатку змініть привʼязку.`,
        closeAfter: 6000,
      })
      return
    }

    if (draft.tiers.length <= 1) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message: 'Потрібна хоча б одна ступінь',
        closeAfter: 4000,
      })
      return
    }

    removeTier(tierId)
  }

  const onRemoveGroup = (group) => {
    if (draft.rewardGroups.length <= 1) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message: 'Потрібна хоча б одна група винагороди',
        closeAfter: 4000,
      })
      return
    }

    const fundGroupRefs = countFundItemsByRewardGroupId(fundItems, group.id)
    if (fundGroupRefs > 0) {
      setStatusMessage({
        show: true,
        type: 'fail',
        message: `Цю групу вибрано в ${fundGroupRefs} позиціях фонду (включно з архівом). Спочатку змініть привʼязку.`,
        closeAfter: 6000,
      })
      return
    }

    const confirmed = window.confirm(
      `Видалити групу «${group.name || 'без назви'}»? Колонку кількості та квоти цієї групи буде прибрано зі всіх ступенів.`
    )

    if (!confirmed) {
      return
    }

    removeGroup(group.id)
  }

  return (
    <div className={styles.root}>
      {statusMessage.show ? (
        <StatusMessage className="Site-StatusMessage" type={statusMessage.type} message={statusMessage.message} />
      ) : null}

      {loadStatus === 'missing' ? (
        <p className={styles.saveHint}>
          Документ ще не створено. Показано початкові значення; збереження створить конфігурацію.
        </p>
      ) : null}

      <ModulePanel title="Загальні налаштування">
        <div className={styles.outletRow}>
          <Switch
            checked={Boolean(draft.applyToOutlet)}
            onChange={setApplyToOutlet}
            ariaLabel="Поширюється на Outlet"
          />
          <div>
            <div className={styles.outletTitle}>Поширюється на Outlet</div>
            <p className={styles.hint}>
              Базовий подарунок доступний до будь-якого непорожнього замовлення, зокрема лише з Ame Outlet. Коли опція
              вимкнена, сума товарів Ame Outlet не відкриває наступні рівні — для порогів враховується вартість товарів
              основного каталогу після знижок і промокодів, без доставки, пакування та безкоштовних позицій. Коли опція
              увімкнена, сума Ame Outlet також бере участь у досягненні порогів.
            </p>
          </div>
        </div>
      </ModulePanel>

      <ModulePanel
        title="Ступені та кількість подарунків"
        buttons={
          <Button
            theme="circle"
            fill="accent"
            icon="plus"
            tooltip="Додати ступінь"
            onClick={addTier}
          />
        }
      >
        <p className={styles.hint}>
          Пороги — невідʼємні цілі гривні. Квоти — невідʼємні цілі штуки для поточної ступені (без сумування з
          попередніми). Верхня межа наступного порога до поточної ступені не входить.
        </p>

        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Поріг від, грн</th>
                <th>Діапазон</th>
                {sortedGroups.map((group) => (
                  <th key={group.id}>{getRewardGroupColumnLabel(group.name)}</th>
                ))}
                <th>Дії</th>
              </tr>
            </thead>
            <tbody>
              {sortedTiers.map((tier) => (
                <tr key={tier.id}>
                  <td>
                    <input
                      className={styles.inputCompact}
                      type="number"
                      min={0}
                      step={1}
                      value={tier.minAmount}
                      onChange={(event) =>
                        parseAmountInput(event.target.value, (value) =>
                          updateTierMinAmount(tier.id, value)
                        )
                      }
                    />
                  </td>
                  <td className={styles.rangeCell}>{getTierRangeLabel(tier, sortedTiers)}</td>
                  {sortedGroups.map((group) => (
                    <td key={group.id}>
                      <input
                        className={styles.inputCompact}
                        type="number"
                        min={0}
                        step={1}
                        value={tier.quotas?.[group.id] ?? 0}
                        onChange={(event) =>
                          parseAmountInput(event.target.value, (value) =>
                            updateTierQuota(tier.id, group.id, value)
                          )
                        }
                      />
                    </td>
                  ))}
                  <td className={styles.actionsCell}>
                    <Button
                      theme="circle"
                      fill="white"
                      icon="trash-alt"
                      tooltip="Видалити ступінь"
                      onClick={() => onRemoveTier(tier.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <ModuleSpoiler
          className={styles.groupsSpoiler}
          title="Групи винагород"
          defaultExpanded={false}
          actions={({ expand, isExpanded }) => (
            <Button
              theme="circle"
              fill="accent"
              icon="plus"
              tooltip="Додати групу"
              onClick={() => {
                if (!isExpanded) {
                  expand()
                }
                addGroup()
              }}
            />
          )}
        >
          <div className={styles.groupsList}>
            {sortedGroups.map((group) => (
              <div key={group.id} className={styles.groupRow}>
                <input
                  className={styles.inputGroupName}
                  type="text"
                  value={group.name}
                  onChange={(event) => updateGroupName(group.id, event.target.value)}
                />
                <Button
                  theme="circle"
                  fill="white"
                  icon="arrow-up"
                  tooltip="Вище"
                  onClick={() => moveGroup(group.id, 'up')}
                />
                <Button
                  theme="circle"
                  fill="white"
                  icon="arrow-down"
                  tooltip="Нижче"
                  onClick={() => moveGroup(group.id, 'down')}
                />
                <Button
                  theme="circle"
                  fill="white"
                  icon="trash-alt"
                  tooltip="Видалити групу"
                  onClick={() => onRemoveGroup(group)}
                />
              </div>
            ))}
          </div>
        </ModuleSpoiler>
      </ModulePanel>

      <div className={styles.footer}>
        <Button
          title="Зберегти"
          theme="solid"
          fill="accent"
          size={32}
          isLoading={isSaving}
          onClick={onSave}
        />
        {isDirty && !isSaving ? (
          <Button title="Скасувати зміни" theme="bounded" size={32} onClick={onCancelChanges} />
        ) : (
          <button
            type="button"
            className={['Button', 'Button_theme_bounded', 'Button_size_32', styles.saveDisabled].join(' ')}
            disabled
          >
            <span className="Button-Content">Скасувати зміни</span>
          </button>
        )}
      </div>
    </div>
  )
}

export default GiftsRules
